import React from "react";
import Button from "../../components/staff/Button";
import { formatCalendarDateToString } from "../../utils/dates";
import type { FirmContactValidation } from "../../utils/firmContactUtils";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import {
  formatAccountingServiceTypeLabel,
  officeDisplayName,
} from "../../utils/accountingOfficeDetail";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import {
  AccountingDetailField,
  AccountingFirmContactNotices,
  AccountingPanel,
  FIELD_VALUE_CLASS,
} from "./accountingFirmPanels";

function overviewField (value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
}

function capitalizeFirstWord (value: string): string {
  if (!value || value === "—") {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

type AccountingFirmOverviewProps = {
  office: AccountingOfficeDetail;
  contactValidation: FirmContactValidation;
  inheritContactsNotice?: string | null;
  ccContactDisplay?: string;
  onEditParameters: () => void;
  onOpenCreditLedger: () => void;
};

export default function AccountingFirmOverview ({
  office,
  contactValidation,
  inheritContactsNotice,
  ccContactDisplay,
  onEditParameters,
  onOpenCreditLedger,
}: AccountingFirmOverviewProps): React.ReactElement {
  const jurisdictionSize =
    office.firmProfessionalsInPrimary ??
    (office.professionalsInPrimary != null
      ? String(office.professionalsInPrimary)
      : "");

  const membershipType = capitalizeFirstWord(
    overviewField(office.membershipType),
  );
  const isPrimaryMembership =
    String(office.membershipType ?? "").toLowerCase() === "primary";

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <AccountingFirmContactNotices
        validation={contactValidation}
        inheritContactsNotice={inheritContactsNotice}
      />
      <AccountingPanel
        title="Overview"
        footer={
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              text="Edit Parameters"
              color="secondary"
              variant="filled"
              size="fluid"
              className="w-full sm:flex-1"
              onClick={onEditParameters}
            />
            <Button
              text="Balance Management"
              color="primary"
              variant="outlined"
              size="fluid"
              className="w-full sm:flex-1"
              onClick={onOpenCreditLedger}
            />
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <AccountingDetailField
            label="Name"
            value={overviewField(officeDisplayName(office))}
          />
          <AccountingDetailField
            label="CC - Contact"
            value={overviewField(ccContactDisplay)}
          />
          <AccountingDetailField
            label="Legal Name"
            value={overviewField(office.firmLegalName)}
          />
          <AccountingDetailField
            label="Membership Type"
            value={membershipType}
            valueClassName={
              isPrimaryMembership
                ? `${FIELD_VALUE_CLASS} text-Default`
                : FIELD_VALUE_CLASS
            }
          />
          <AccountingDetailField
            label="Service Type"
            value={overviewField(
              formatAccountingServiceTypeLabel(office.serviceType),
            )}
          />
          <AccountingDetailField
            label="First Dues Date"
            value={overviewField(
              office.billingStartDate
                ? formatCalendarDateToString(office.billingStartDate)
                : "",
            )}
          />
          <AccountingDetailField
            label="Jurisdiction"
            value={overviewField(getJurisdictionLabel(office))}
          />
          <AccountingDetailField
            label="Jurisdiction Size"
            value={overviewField(jurisdictionSize)}
          />
          <AccountingDetailField
            label="Joined"
            value={overviewField(
              office.dateJoined
                ? formatCalendarDateToString(office.dateJoined)
                : "",
            )}
          />
          <AccountingDetailField
            label="Primary Contact"
            value={overviewField(
              contactValidation.primaryContactName ||
              contactValidation.primaryContactId,
            )}
          />
        </div>
      </AccountingPanel>
    </div>
  );
}
